/*
 * GF-CV 1.1 (22/10/2026): + GF-CQ 1.0 option 'Quote' (With quote / No quote / Custom quote); o nhap Custom quote bat/tat theo lua chon.
 * GF-CV 1.0 (21/10/2026, Claude) - GenusFaith canvas PDP selector, used by snippets/gf-cv-chon.liquid (phan 'chon').
 *
 * Horizon's <variant-picker> stays the single source of truth. Our tiles never hold their own variant state:
 *   tile click -> find the native radio (data-option-name + value) inside the picker -> checked = true + 'change'
 *   -> Horizon fetches the section, morphs the picker, updates ?variant=, price, media, add-to-cart.
 * The tiles are re-drawn from the picker's checked inputs after every change, after every morph
 * (MutationObserver on the section) and on pageshow (back/forward cache).
 * The native picker is hidden by CSS only after this file has run (data-gf-cv-ready on our root).
 * Self-check in the console: window.GF_CV.version === '1.0'; window.GF_CV.state() lists each root's selection.
 */
const VERSION = '1.1';
const FRAME = 'Frame';
const SIZE = 'Size';
const PERS = 'Personalization';
const QUOTE = 'Quote';

const norm = (s) => String(s ?? '').trim().toLowerCase();
// Only touch text that changed: the MutationObserver below must not see our own no-op writes.
const setText = (el, txt) => {
  if (el && el.textContent !== txt) el.textContent = txt;
};

function fmtMoney(cents, fmt) {
  const n = Number(cents) / 100;
  const group = (intStr, sep) => intStr.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
  const fixed = (digits, th, dec) => {
    const [i, d] = n.toFixed(digits).split('.');
    return group(i, th) + (d ? dec + d : '');
  };
  return String(fmt || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => {
    switch (k) {
      case 'amount_no_decimals':
        return fixed(0, ',', '.');
      case 'amount_with_comma_separator':
        return fixed(2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return fixed(0, '.', ',');
      case 'amount_with_apostrophe_separator':
        return fixed(2, "'", '.');
      default:
        return fixed(2, ',', '.');
    }
  });
}

class GfCv {
  constructor(root) {
    this.root = root;
    this.pid = root.dataset.pid;
    this.scope = root.closest('.shopify-section') || document;
    this.data = JSON.parse(root.querySelector('[data-gf-cv-data]').textContent);
    const colors = [];
    const fi = this.data.opts.indexOf(FRAME);
    this.data.v.forEach((v) => {
      const f = v.o[fi];
      if (f !== this.data.nf && !colors.includes(f)) colors.push(f);
    });
    this.colors = colors;
    this.lastColor = null;
    this.raf = 0;

    root.addEventListener('change', (e) => this.onTile(e));
    const guideBtn = root.querySelector('[data-gf-cv-guide]');
    const dlg = root.querySelector('dialog');
    if (guideBtn && dlg && typeof dlg.showModal === 'function') {
      guideBtn.addEventListener('click', () => dlg.showModal());
      dlg.addEventListener('click', (e) => {
        if (e.target === dlg || (e.target instanceof Element && e.target.closest('[data-gf-cv-dong]'))) dlg.close();
      });
      dlg.addEventListener('close', () => guideBtn.focus());
    } else if (guideBtn) {
      guideBtn.hidden = true;
    }

    // Horizon morphs the picker after its fetch; re-read the checked inputs whenever that happens.
    this.mo = new MutationObserver(() => this.schedule());
    this.mo.observe(this.scope, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['checked', 'data-current-checked', 'selected', 'data-product-id'],
    });

    this.render();
    if (this.picker()) root.setAttribute('data-gf-cv-ready', VERSION);
  }

  picker() {
    return (
      this.scope.querySelector(`variant-picker[data-product-id="${this.pid}"]`) ||
      this.scope.querySelector('variant-picker[data-template-product-match="true"]')
    );
  }

  /** Current selection {optionName: value}, read from the native picker. */
  readSel() {
    const sel = {};
    const p = this.picker();
    if (p) {
      p.querySelectorAll('fieldset input:checked').forEach((i) => {
        if (i.dataset.optionName) sel[i.dataset.optionName] = i.value;
      });
      p.querySelectorAll('select').forEach((s) => {
        const o = s.selectedOptions[0];
        if (o && o.dataset.optionName) sel[o.dataset.optionName] = s.value;
      });
    }
    this.data.opts.forEach((name, i) => {
      if (!(name in sel)) sel[name] = this.data.cur[i];
    });
    return sel;
  }

  findV(sel) {
    return this.data.v.find((v) => this.data.opts.every((name, i) => v.o[i] === sel[name])) || null;
  }

  /** Drive the native picker. Returns true when a change was dispatched. */
  setOption(name, value) {
    const p = this.picker();
    if (!p || value == null) return false;
    const radio = Array.from(p.querySelectorAll('input[type="radio"]')).find(
      (i) => i.dataset.optionName === name && i.value === value
    );
    if (radio) {
      if (radio.checked) return false;
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }
    const select = Array.from(p.querySelectorAll('select')).find((s) =>
      Array.from(s.options).some((o) => o.dataset.optionName === name)
    );
    if (select && select.value !== value) {
      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }
    return false;
  }

  onTile(e) {
    const i = e.target;
    if (!(i instanceof HTMLInputElement) || !i.dataset.g) return;
    const g = i.dataset.g;
    if (g === 'style') {
      this.setOption(FRAME, i.value === 'frameless' ? this.data.nf : this.lastColor || this.colors[0]);
    } else if (g === 'color') {
      this.setOption(FRAME, i.value);
    } else if (g === 'size') {
      this.setOption(SIZE, i.value);
    } else if (g === 'pers') {
      this.setOption(PERS, i.value);
    } else if (g === 'quote') {
      this.setOption(QUOTE, i.value);
    }
    this.render();
  }

  schedule() {
    if (this.raf) return;
    this.raf = requestAnimationFrame(() => {
      this.raf = 0;
      this.render();
    });
  }

  check(group, value) {
    this.root.querySelectorAll(`input[data-g="${group}"]`).forEach((i) => {
      const on = value != null && i.value === value;
      if (i.checked !== on) i.checked = on;
    });
  }

  render() {
    const d = this.data;
    const sel = this.readSel();
    const f = sel[FRAME];
    const framed = d.nf ? f !== d.nf : true;
    if (framed) this.lastColor = f;

    this.check('style', framed ? 'framed' : 'frameless');
    const colorStep = this.root.querySelector('[data-gf-cv-step="color"]');
    if (colorStep) colorStep.hidden = !framed;
    this.check('color', framed ? f : null);
    const colorVal = this.root.querySelector('[data-gf-cv-val="color"]');
    setText(colorVal, framed ? `: ${f}` : '');

    this.check('size', sel[SIZE]);
    this.root.querySelectorAll('input[data-g="size"]').forEach((i) => {
      const v = this.findV({ ...sel, [SIZE]: i.value });
      const label = i.closest('.gf-cv__tile');
      const out = label && label.querySelector('[data-gf-cv-sp]');
      const off = !v || !v.a;
      if (label) label.classList.toggle('is-off', off);
      setText(out, (v ? fmtMoney(v.p, d.fmt) : '') + (off ? ' · Sold out' : ''));
    });
    this.root.querySelectorAll('.gf-cv-hop__h, .gf-cv-hop tr[data-size]').forEach((el) => {
      el.classList.toggle('is-on', el.dataset.size === sel[SIZE]);
    });

    if (d.pno && d.opts.includes(PERS)) {
      const p = sel[PERS];
      this.check('pers', norm(p) === norm(d.pno) ? d.pno : d.pyes);
      const no = this.findV({ ...sel, [PERS]: d.pno });
      const yes = this.findV({ ...sel, [PERS]: d.pyes });
      const fee = this.root.querySelector('[data-gf-cv-phi]');
      setText(fee, no && yes && yes.p > no.p ? `+${fmtMoney(yes.p - no.p, d.fmt)}` : '');
    }

    if (d.opts.includes(QUOTE)) {
      const q = sel[QUOTE];
      this.check('quote', q);
      const base = this.findV({ ...sel, [QUOTE]: d.v[0] ? d.v[0].o[d.opts.indexOf(QUOTE)] : q });
      this.root.querySelectorAll('[data-gf-cv-qphi]').forEach((el) => {
        const v = this.findV({ ...sel, [QUOTE]: el.dataset.gfCvQphi });
        setText(el, v && base && v.p > base.p ? `+${fmtMoney(v.p - base.p, d.fmt)}` : '');
      });
      const kind = norm(q).startsWith('custom') ? 'custom' : norm(q).startsWith('no') ? 'no' : 'with';
      this.root.querySelectorAll('[data-gf-cq-ghi]').forEach((el) => {
        const h = el.dataset.gfCqGhi !== kind;
        if (el.hidden !== h) el.hidden = h;
      });
      const box = this.root.querySelector('[data-gf-cq-box]');
      if (box) {
        const off = kind !== 'custom';
        if (box.hidden !== off) box.hidden = off;
        box.querySelectorAll('textarea, input').forEach((el) => {
          if (el.disabled !== off) el.disabled = off;
        });
      }
    }

    // Step numbers follow the visible steps (1. style, 2. frame color only when framed, ...).
    let n = 0;
    this.root.querySelectorAll('[data-gf-cv-step]').forEach((step) => {
      if (step.hidden) return;
      n += 1;
      setText(step.querySelector('[data-gf-cv-n]'), `${n}. `);
    });

    const cur = this.findV(sel);
    this.scope.querySelectorAll(`[data-gf-cv-ship][data-pid="${this.pid}"]`).forEach((el) => {
      const chu = el.querySelector('[data-gf-cv-ship-chu]');
      if (!chu || !cur) return;
      const txt = cur.p >= Number(el.dataset.free || d.free || 7000) ? el.dataset.co : el.dataset.chua;
      if (txt) setText(chu, txt);
    });
    this.sel = sel;
  }
}

const roots = new Set();
function init() {
  document.querySelectorAll('[data-gf-cv-root]').forEach((root) => {
    if (root.__gfCv) return;
    try {
      root.__gfCv = new GfCv(root);
      roots.add(root.__gfCv);
    } catch (err) {
      console.warn('[gf-cv]', err);
    }
  });
}

if (!window.GF_CV) {
  window.GF_CV = {
    version: VERSION,
    init,
    state: () => Array.from(roots).map((c) => ({ pid: c.pid, sel: c.sel, variant: c.findV(c.sel || {})?.id })),
  };
  // Any change on a native picker (also from other scripts) -> redraw.
  document.addEventListener('change', (e) => {
    if (e.target instanceof Element && e.target.closest('variant-picker')) roots.forEach((c) => c.schedule());
  });
  window.addEventListener('pageshow', () => roots.forEach((c) => c.render()));
  document.addEventListener('shopify:section:load', init);
}
init();
